import React, { Fragment, useEffect, useMemo, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import './ArchiveFolders.css';
import {
  PLAN_FOLDERS,
  PLAN_FOLDER_CREATE,
  PLAN_FOLDER_UPDATE,
  PLAN_FOLDER_DELETE,
  PLAN_META_LIST,
} from '../../auth/urlParameters';
import { ToastError, ToastConfirm, Alerte_Warning } from '../../../ui/AlertesUi';
import { SimpleSpinner } from '../../../ui/Spinner';

const defaultCreateForm = {
  reference: '',
  title: '',
  securityLevel: 'public',
  classificationCode: '',
  owningDepartment: '',
  parentId: '',
  planId: '',
};

const flattenPlans = (plans) => {
  const byId = new Map();
  const roots = [];

  plans.forEach((plan) => byId.set(String(plan.id), { ...plan, children: [] }));
  byId.forEach((plan) => {
    const parentId = plan.parentId ? String(plan.parentId) : null;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId).children.push(plan);
    } else {
      roots.push(plan);
    }
  });

  const sortPlans = (items) =>
    items.sort((left, right) =>
      `${left.code || ''} ${left.title || ''}`.localeCompare(`${right.code || ''} ${right.title || ''}`)
    );

  const flatten = (items, depth = 0) =>
    sortPlans(items).reduce((acc, plan) => {
      acc.push({ plan, depth });
      if (plan.children.length > 0) {
        acc.push(...flatten(plan.children, depth + 1));
      }
      return acc;
    }, []);

  return flatten(roots);
};

const ArchiveFolders = ({ embedded = false }) => {
  const location = useLocation();
  const requestedFolderId = new URLSearchParams(location.search).get('folder');
  const { session } = useSelector((state) => ({
    session: state.sessionAuth,
  }));

  const [tree, setTree] = useState([]);
  const [plans, setPlans] = useState([]);
  const [flatNodes, setFlatNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [collapsedPlanGroups, setCollapsedPlanGroups] = useState(() => new Set());
  const [collapsedFolderNodes, setCollapsedFolderNodes] = useState(() => new Set());
  const [createForm, setCreateForm] = useState(defaultCreateForm);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editForm, setEditForm] = useState(defaultCreateForm);
  const createPlanId = createForm.planId ? Number(createForm.planId) : null;
  const editPlanId = editForm.planId ? Number(editForm.planId) : null;
  const groupedPlanOptions = useMemo(() => flattenPlans(plans), [plans]);
  const activePlanOptions = useMemo(() => groupedPlanOptions.filter(({ plan }) => plan.active), [groupedPlanOptions]);

  const folderGroups = useMemo(() => {
    const groupsByPlanId = new Map();

    tree.forEach((root) => {
      const planId = root.planId ? String(root.planId) : 'unassigned';
      if (!groupsByPlanId.has(planId)) groupsByPlanId.set(planId, []);
      groupsByPlanId.get(planId).push(root);
    });

    const groupOrder = new Map(
      groupedPlanOptions.map(({ plan }, index) => [String(plan.id), index])
    );
    const planById = new Map(plans.map((plan) => [String(plan.id), plan]));

    return Array.from(groupsByPlanId.entries())
      .map(([planId, folders]) => ({
        id: planId,
        plan: planById.get(planId) || null,
        folders,
        order: groupOrder.has(planId) ? groupOrder.get(planId) : Number.MAX_SAFE_INTEGER,
      }))
      .sort((left, right) => left.order - right.order);
  }, [tree, plans, groupedPlanOptions]);

  const canManage = useMemo(() => {
    const roles = session.roles || [];
    return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_EDITED');
  }, [session.roles]);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  const attachParent = (nodes, parentId = null) =>
    nodes.map((node) => ({
      ...node,
      parentId,
      children: node.children ? attachParent(node.children, node.id) : [],
    }));

  const flattenTree = (nodes, depth = 0) =>
    nodes.reduce((acc, node) => {
      acc.push({
        id: node.id,
        label: `${'— '.repeat(depth)}${node.reference} — ${node.title}`,
        depth,
        raw: node,
        planId: node.planId || null,
      });
      if (node.children && node.children.length > 0) {
        acc.push(...flattenTree(node.children, depth + 1));
      }
      return acc;
    }, []);

  const fetchTree = () => {
    setLoading(true);
    axios
      .get(PLAN_FOLDERS(), authHeaders)
      .then((res) => {
        const nodes = attachParent(res.data?.folders || []);
        setTree(nodes);
        const flat = flattenTree(nodes);
        setFlatNodes(flat);
        const requested = flat.find((item) => String(item.id) === requestedFolderId);
        if (requested) {
          handleSelect(requested.raw);
        } else if (!selected && flat.length > 0) {
          handleSelect(flat[0].raw);
        }
      })
      .catch((e) => {
        ToastError("Impossible de charger les dossiers d'archive");
        console.error(e);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTree();
    axios
      .get(PLAN_META_LIST(), authHeaders)
      .then((res) => {
        const list = res?.data?.plans || [];
        setPlans(list);
        const firstActive = list.find((plan) => plan.active);
        if (!createForm.planId && firstActive) {
          setCreateForm((prev) => ({ ...prev, planId: firstActive.id }));
        }
      })
      .catch(() => setPlans([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (node) => {
    setSelected(node);
    setEditForm({
      reference: node.reference || '',
      title: node.title || '',
      securityLevel: node.securityLevel || 'public',
      classificationCode: node.classificationCode || '',
      owningDepartment: node.owningDepartment || '',
      parentId: node.parentId || '',
      planId: node.planId || '',
    });
  };

  const togglePlanGroup = (planId) => {
    setCollapsedPlanGroups((previous) => {
      const next = new Set(previous);
      if (next.has(planId)) {
        next.delete(planId);
      } else {
        next.add(planId);
      }
      return next;
    });
  };

  const toggleFolderNode = (folderId) => {
    setCollapsedFolderNodes((previous) => {
      const next = new Set(previous);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const openCreateModal = () => {
    const selectedActive = plans.find((plan) => Number(plan.id) === Number(selected?.planId) && plan.active);
    const previousActive = plans.find((plan) => Number(plan.id) === Number(createForm.planId) && plan.active);
    setCreateForm((previous) => ({
      ...previous,
      planId: selectedActive?.id || previousActive?.id || plans.find((plan) => plan.active)?.id || '',
      parentId: selectedActive ? selected?.id || '' : '',
    }));
    setCreateModalOpen(true);
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!createForm.planId) {
      return ToastError('Choisissez un plan');
    }
    const payload = { ...createForm, parentId: createForm.parentId || null };
    setCreating(true);
    axios
      .post(PLAN_FOLDER_CREATE(), payload, authHeaders)
      .then(() => {
        ToastConfirm('Dossier créé');
        setCreateModalOpen(false);
        setCreateForm((prev) => ({ ...defaultCreateForm, planId: plans.find((plan) => plan.active)?.id || '' }));
        fetchTree();
      })
      .catch((err) => {
        console.log(err);
        if (!canManage) return Alerte_Warning("Vous n'êtes pas autorisé.");
        ToastError(err.response?.data?.message || 'Erreur création dossier');
      })
      .finally(() => setCreating(false));
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!editForm.planId) {
      return ToastError('Choisissez un plan');
    }
    const payload = { ...editForm, parentId: editForm.parentId || null };
    axios
      .patch(PLAN_FOLDER_UPDATE(selected.id), payload, authHeaders)
      .then(() => {
        ToastConfirm('Dossier mis à jour');
        fetchTree();
      })
      .catch((err) => ToastError(err.response?.data?.message || 'Erreur mise à jour dossier'));
  };

  const handleDelete = () => {
    if (!selected) return;
    if (!window.confirm('Supprimer ce dossier ?')) return;
    axios
      .delete(PLAN_FOLDER_DELETE(selected.id), authHeaders)
      .then(() => {
        ToastConfirm('Dossier supprimé');
        setSelected(null);
        fetchTree();
      })
      .catch((err) => ToastError(err.response?.data?.message || 'Suppression impossible'));
  };

  const renderNode = (node) => {
    const isSelected = selected && selected.id === node.id;
    const hasChildren = (node.children || []).length > 0;
    const isCollapsed = collapsedFolderNodes.has(node.id);
    return (
      <li key={node.id}>
        <div
          className={`folder-card ${hasChildren ? 'branch' : 'leaf'} ${isSelected ? 'selected' : ''}`}
          onClick={() => handleSelect(node)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleSelect(node);
            }
          }}
        >
          <div className="folder-node-title">
            <i
              className={`${hasChildren ? 'ri-folder-3-line' : 'ri-folder-line'} folder-node-icon`}
              aria-hidden="true"
            ></i>
            <span className="folder-ref">{node.reference}</span>
            <span className="folder-title">— {node.title}</span>
            <span className="folder-node-meta">
              {node.securityLevel ? <small className="folder-badge">{node.securityLevel}</small> : null}
              {hasChildren ? (
                <button
                  type="button"
                  className="folder-node-toggle"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleFolderNode(node.id);
                  }}
                  aria-label={isCollapsed ? 'Déplier le dossier' : 'Replier le dossier'}
                  aria-expanded={!isCollapsed}
                >
                  <i className={`ri-${isCollapsed ? 'arrow-right-s' : 'arrow-down-s'}-line`} aria-hidden="true"></i>
                </button>
              ) : null}
            </span>
          </div>
        </div>
        {hasChildren && !isCollapsed && (
          <ul>
            {node.children.map((child) => renderNode(child))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <Fragment>
      {!embedded && <Breadcrumb title ="Dossiers d'archive" />}
      <div className={embedded ? 'folder-embedded-content' : 'content'}>
        <div className="container-fluid">
          <div className="row">
            {!embedded && (
            <div className="col-md-4 col-lg-3 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            )}


            <div className={embedded ? 'col-12' : 'col-md-8 col-lg-9 col-xl-9'}>
              <div className="row folder-layout">

                {canManage && (
                  <div className="col-lg-12 folder-create-toolbar">
                    <button className="btn btn-primary btn-uniform-action" type="button" onClick={openCreateModal}>
                      <i className="ri-add-circle-line" aria-hidden="true"></i> Nouveau dossier
                    </button>
                  </div>
                )}

    
                <div className="col-lg-6 folder-layout-column">
                  <div className="card folder-panel folder-tree-panel">
                    <div className="card-body">
                      <h4 className="card-title d-flex align-items-center">
                        <i className="ri-folder-5-line me-2"></i> Arborescence des dossiers
                      </h4>
                      {loading ? (
                        SimpleSpinner()
                      ) : (
                        <div className="folder-tree-wrapper">
                          {folderGroups.length > 0 ? (
                            <ul className="folder-tree">
                              {folderGroups.map((group) => {
                                const isCollapsed = collapsedPlanGroups.has(group.id);
                                const groupLabel = group.plan
                                  ? `${group.plan.code} — ${group.plan.title}`
                                  : 'Dossiers sans organisation / service';

                                return (
                                  <li key={group.id} className="folder-plan-group">
                                    <div className="folder-plan-root">
                                      <span>
                                        <i className="ri-archive-drawer-line" aria-hidden="true"></i>
                                        {groupLabel}
                                      </span>
                                      <button
                                        type="button"
                                        className="folder-group-toggle"
                                        onClick={() => togglePlanGroup(group.id)}
                                        aria-label={isCollapsed ? 'Déplier le groupe' : 'Replier le groupe'}
                                        aria-expanded={!isCollapsed}
                                      >
                                        <i className={`ri-${isCollapsed ? 'arrow-right-s' : 'arrow-down-s'}-line`} aria-hidden="true"></i>
                                      </button>
                                    </div>
                                    {!isCollapsed && (
                                      <ul>
                                        {group.folders.map((root) => renderNode(root))}
                                      </ul>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <div className="alert alert-warning">Aucun dossier à afficher</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="card">
                    <div className="card-body">
                      <h5 className="card-title d-flex align-items-center">
                        <i className="ri-information-line me-2"></i> Détails & édition
                      </h5>
                      {selected ? (
                        <>
                  

                          {canManage && (
                            <form onSubmit={handleUpdate}>
                              <div className="row">
                                <div className="col-md-6">
                                  <div className="form-group">
                                    <label>Référence</label>
                                    <input
                                      className="form-control"
                                      value={editForm.reference}
                                      onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })}
                                      required
                                    />
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="form-group">
                                    <label>Titre</label>
                                    <input
                                      className="form-control"
                                      value={editForm.title}
                                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                             
                              <div className="row">
                                
                                <div className="col-md-6">
                                  <div className="form-group">
                                    <label>Organisation / Service </label>
                                    <select
                                      className="form-control"
                                      value={editForm.planId}
                                      onChange={(e) => {
                                        const value = e.target.value ? Number(e.target.value) : '';
                                        setEditForm({ ...editForm, planId: value, parentId: '' });
                                      }}
                                    >
                                      <option value="">Choisir un plan</option>
                                      {groupedPlanOptions.map(({ plan, depth }) => (
                                        <option key={plan.id} value={plan.id}>
                                          {'— '.repeat(depth)}{plan.code} — {plan.title}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="form-group">
                                    <label>Dossier Parent</label>
                                    <select
                                      className="form-control"
                                      value={editForm.parentId}
                                      onChange={(e) => {
                                        const value = e.target.value ? Number(e.target.value) : '';
                                        setEditForm({ ...editForm, parentId: value });
                                      }}
                                    >
                                      <option value="">Aucun (racine)</option>
                                      {flatNodes
                                        .filter((n) => n.id !== selected.id && (!editPlanId || Number(n.planId) === editPlanId))
                                        .map((n) => (
                                          <option key={n.id} value={n.id}>
                                            {n.label}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex gap-2">
                                <button className="btn btn-primary" type="submit">
                                  Mettre à jour
                                </button> &nbsp;&nbsp; &nbsp;&nbsp;
                                <button className="btn btn-outline-danger" type="button" onClick={handleDelete}>
                                  Supprimer
                                </button>
                              </div>
                            </form>
                          )}
                        </>
                      ) : (
                        <div className="alert alert-info">Sélectionnez un dossier dans l'arbre.</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {canManage && (
        <Modal
          isOpen={createModalOpen}
          toggle={() => !creating && setCreateModalOpen(false)}
          className="folder-create-modal"
          size="lg"
          centered
        >
          <ModalHeader toggle={() => !creating && setCreateModalOpen(false)}>
            <i className="ri-add-circle-line" aria-hidden="true"></i> Nouveau dossier
          </ModalHeader>
          <ModalBody>
            <form onSubmit={handleCreate}>
              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="folder-create-reference">Référence</label>
                  <input
                    id="folder-create-reference"
                    className="form-control"
                    value={createForm.reference}
                    onChange={(e) => setCreateForm({ ...createForm, reference: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="folder-create-title">Titre</label>
                  <input
                    id="folder-create-title"
                    className="form-control"
                    value={createForm.title}
                    onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="folder-create-plan">Organisation / Service</label>
                  <select
                    id="folder-create-plan"
                    className="form-control"
                    value={createForm.planId}
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : '';
                      setCreateForm({ ...createForm, planId: value, parentId: '' });
                    }}
                    required
                  >
                    <option value="">Choisir un plan</option>
                    {activePlanOptions.map(({ plan, depth }) => (
                      <option key={plan.id} value={plan.id}>
                        {'— '.repeat(depth)}{plan.code} — {plan.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="folder-create-parent">Dossier parent</label>
                  <select
                    id="folder-create-parent"
                    className="form-control"
                    value={createForm.parentId}
                    onChange={(e) => {
                      const value = e.target.value ? Number(e.target.value) : '';
                      setCreateForm({ ...createForm, parentId: value });
                    }}
                  >
                    <option value="">Aucun (racine)</option>
                    {flatNodes
                      .filter((node) => !createPlanId || Number(node.planId) === createPlanId)
                      .map((node) => (
                        <option key={node.id} value={node.id}>
                          {node.label}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="folder-create-actions">
                <button className="btn btn-outline-light" type="button" onClick={() => setCreateModalOpen(false)} disabled={creating}>
                  Annuler
                </button>
                <button className="btn btn-primary" type="submit" disabled={creating}>
                  {creating ? 'Création…' : 'Créer le dossier'}
                </button>
              </div>
            </form>
          </ModalBody>
        </Modal>
      )}
    </Fragment>
  );
};

export default ArchiveFolders;
